import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import { apiV1Request } from '#shared/api/requests';
import {
    getOpenApiAddonListUrl,
    getOpenApiCreateAddonUrl,
    getOpenApiDeleteAddonUrl,
    getOpenApiGetAddonUrl,
    getOpenApiUpdateAddonUrl,
    OpenApiAddonListMethod,
    type OpenApiAddonListResponseSuccess,
    type OpenApiAddonListSearchParams,
    OpenApiCreateAddonMethod,
    type OpenApiCreateAddonRequest,
    type OpenApiCreateAddonResponseSuccess,
    OpenApiDeleteAddonMethod,
    type OpenApiDeleteAddonPathParameters,
    type OpenApiDeleteAddonResponseSuccess,
    OpenApiGetAddonMethod,
    type OpenApiGetAddonPathParameters,
    type OpenApiGetAddonResponseSuccess,
    type OpenApiSchemaAddon,
    OpenApiUpdateAddonMethod,
    type OpenApiUpdateAddonPathParameters,
    type OpenApiUpdateAddonRequest,
    type OpenApiUpdateAddonResponseSuccess,
} from '#shared/api/schema';

const AddonTag = 'Addon' as const;
const AddonListTag = 'AddonList' as const;

const getAddonTag = ({ id }: Pick<OpenApiSchemaAddon, 'id'>) => {
    return { type: AddonTag, id };
};

export const addonsApi = createApi({
    name: 'entities/addon/api/addons',
    baseQuery: createBaseQuery(apiV1Request),
    tagTypes: [AddonTag, AddonListTag],
    endpoints: (builder) => {
        return {
            getList: builder.query<OpenApiAddonListResponseSuccess, { searchParams?: OpenApiAddonListSearchParams }>({
                query: ({ searchParams }) => {
                    return {
                        url: getOpenApiAddonListUrl(),
                        method: OpenApiAddonListMethod,
                        params: searchParams,
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return result.addons.reduce<{ type: typeof AddonTag | typeof AddonListTag; id: string }[]>(
                        (acc, item) => {
                            acc.push(getAddonTag(item));

                            return acc;
                        },
                        [{ type: AddonListTag, id: 'grid' }]
                    );
                },
            }),
            get: builder.query<OpenApiGetAddonResponseSuccess, { params: OpenApiGetAddonPathParameters }>({
                query: ({ params }) => {
                    return {
                        url: getOpenApiGetAddonUrl(params),
                        method: OpenApiGetAddonMethod,
                    };
                },
                providesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [getAddonTag(result)];
                },
            }),
            post: builder.mutation<OpenApiCreateAddonResponseSuccess, { body: OpenApiCreateAddonRequest }>({
                query: ({ body }) => {
                    return {
                        url: getOpenApiCreateAddonUrl(),
                        method: OpenApiCreateAddonMethod,
                        body,
                    };
                },
                invalidatesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [AddonListTag, getAddonTag(result)];
                },
            }),
            patch: builder.mutation<
                OpenApiUpdateAddonResponseSuccess,
                {
                    params: OpenApiUpdateAddonPathParameters;
                    body: OpenApiUpdateAddonRequest;
                }
            >({
                query: ({ params, body }) => {
                    return {
                        url: getOpenApiUpdateAddonUrl(params),
                        method: OpenApiUpdateAddonMethod,
                        body,
                    };
                },
                invalidatesTags: (result, error) => {
                    if (error || !result) {
                        return [];
                    }

                    return [AddonListTag, getAddonTag(result)];
                },
            }),
            delete: builder.mutation<OpenApiDeleteAddonResponseSuccess, { params: OpenApiDeleteAddonPathParameters }>({
                query: ({ params }) => {
                    return {
                        url: getOpenApiDeleteAddonUrl(params),
                        method: OpenApiDeleteAddonMethod,
                    };
                },
                invalidatesTags: (_result, error, { params }) => {
                    if (error) {
                        return [];
                    }

                    return [AddonListTag, getAddonTag({ id: params.id })];
                },
            }),
        };
    },
});

export const useGetAddonsList = addonsApi.useGetListQuery;
export const useGetAddon = addonsApi.useGetQuery;
export const useLazyGetAddonQuery = addonsApi.useLazyGetQuery;
export const createAddon = addonsApi.endpoints.post.mutate;
export const updateAddon = addonsApi.endpoints.patch.mutate;
export const deleteAddon = addonsApi.endpoints.delete.mutate;
